/**
=========================================================
* Material Dashboard 2 PRO React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { forwardRef } from 'react';

// prop-types is a library for typechecking of props
import PropTypes from 'prop-types';

// Custom styles for MDButton
import MDButtonRoot from 'components/MDButton/MDButtonRoot';
import { CircularProgress, Tooltip } from '@mui/material';

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from 'context';
import WhiteFab from '../../../components/common/WhiteFab';

const MDButton = forwardRef(({ color, variant, size, circular, iconOnly, children, loading, wideButton, ...rest }, ref) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  if (loading) {
    if (rest.startIcon) {
      rest.startIcon = <CircularProgress size={20} color='inherit' />;
      rest.disabled = true;
    } else if (rest.endIcon) {
      rest.endIcon = <CircularProgress size={20} color='inherit' />;
      rest.disabled = true;
    } else {
      rest.startIcon = <CircularProgress size={20} color='inherit' />;
      rest.disabled = true;
    }
  }

  if (rest.minimal) {
    return (
      <Tooltip title={children} arrow={false}>
        <WhiteFab icon={rest.startIcon} textColor={color} size='medium' {...rest} />
      </Tooltip>
    );
  }

  return (
    <MDButtonRoot
      {...rest}
      ref={ref}
      color={color}
      variant={variant === 'gradient' ? 'contained' : variant}
      size={size}
      ownerState={{ color, variant, size, circular, iconOnly, darkMode, wideButton }}
    >
      {children}
    </MDButtonRoot>
  );
});

// Setting default values for the props of MDButton
MDButton.defaultProps = {
  size: 'medium',
  variant: 'contained',
  color: 'primary',
  circular: false,
  iconOnly: false,
  loading: false,
};

// Typechecking props for the MDButton
MDButton.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  variant: PropTypes.oneOf(['text', 'contained', 'outlined', 'gradient']),
  color: PropTypes.oneOf(['white', 'primary', 'secondary', 'info', 'success', 'warning', 'error', 'light', 'dark']),
  circular: PropTypes.bool,
  iconOnly: PropTypes.bool,
  children: PropTypes.node.isRequired,
  loading: PropTypes.bool,
};

export default MDButton;
